import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building, Calendar, Mail, User, Database, ExternalLink, Settings, Loader2, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase, invokeFunction } from '@/integrations/supabase/client';
import DailyNervView from './DailyNervView';
import AnomalySummarySection from './AnomalySummarySection';
import OrgConnectionSettingsDialog from './OrgConnectionSettingsDialog';
import { API_BASE_URL, headers as apiHeaders } from '@/utils/apiConfig';
import { formatFivetranSyncDetail, formatRescheduledForDisplay, isRescheduledSyncState } from '@/utils/fivetranSyncStatus';

interface OrganisationOverviewProps {
  organisation: any;
  onConnectionRemoved?: () => void;
}

/** Matches import-inventory-csv-v3 `source` labels for variance rows. */
function inventorySourceFromEcommerceSubtype(
  connection: { ecommerce_subtype?: string } | undefined,
): string | undefined {
  const s = String(connection?.ecommerce_subtype || '').toLowerCase();
  if (s === 'shopify') return 'shopify_csv_import';
  if (s === 'amazon') return 'amazon_csv_import';
  return undefined;
}

/** Parse YYYY-MM-DD from connector ids like `…_inventory_amazon_20260511`. */
function parseSnapshotDateFromInventoryImportId(importId: string): string | undefined {
  const m = String(importId).match(/_inventory_(?:amazon|shopify)_(\d{8})(?:_|$)/i);
  if (!m) return undefined;
  const d = m[1];
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

function hasEcommerceConnections(connections: Record<string, any> | null): boolean {
  const connectionList = getConnectionList(connections);
  return connectionList.some(isEcommerceFivetranConnection);
}

function getConnectionList(connections: Record<string, any> | null): any[] {
  if (!connections) return [];
  return Array.isArray(connections) ? connections : Object.values(connections);
}

function isCsvConnection(connection: any): boolean {
  if (!connection) return false;
  const service = (connection.service || '').toLowerCase();
  const type = (connection.type || '').toLowerCase();
  return (
    service === 'csv' ||
    type === 'csv' ||
    type === 'csv_import' ||
    connection.connectorId?.startsWith('csv-import-') ||
    connection.connectionId?.startsWith('csv-import-') ||
    connection.id?.startsWith('csv-import-') ||
    !!connection.csvMetadata ||
    (Array.isArray(connection.csvImports) && connection.csvImports.length > 0)
  );
}

function isEcommerceFivetranConnection(connection: any): boolean {
  if (!connection || isCsvConnection(connection)) return false;
  const service = (connection.service || '').toLowerCase();
  const type = (connection.type || '').toLowerCase();
  const connectorName = (connection.connector_name || '').toLowerCase();
  const name = (connection.name || '').toLowerCase();
  const lookup = `${service} ${type} ${connectorName} ${name}`;

  return lookup.includes('shopify') || lookup.includes('amazon');
}

const OrganisationOverview: React.FC<OrganisationOverviewProps> = ({ organisation, onConnectionRemoved }) => {
  const connections = getConnectionList(organisation?.connections);
  const hasAnyConnections = connections.length > 0;
  const hasOnlyCsvConnections = hasAnyConnections && connections.every(isCsvConnection);
  const hasEcommerceFivetranConnections = connections.some(isEcommerceFivetranConnection);
  const hideDailyNervForCsvOnlyOrg = hasOnlyCsvConnections && !hasEcommerceFivetranConnections;

  return (
    <div data-testid="organisation-overview" className="space-y-6">
      {/* Daily NERV Graph Section - Only show if financial snapshots enabled */}
      {organisation.financial_snapshot_table_created &&
        !hideDailyNervForCsvOnlyOrg &&
        (
          <DailyNervView organisation={organisation} />
        )}

      {/* Existing Overview Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="h-5 w-5 text-blue-500" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">EIN</h3>
                  <p className="font-mono">{organisation.ein}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">State of Incorporation</h3>
                  <p>{organisation.state_of_incorporation}</p>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500">Address</h3>
                <p>{organisation.address}</p>
              </div>

              {organisation.revenue_last_12_months && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Revenue (last 12 months)</h3>
                  <p className="font-mono">
                    ${organisation.revenue_last_12_months.toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-medium text-gray-500">Registration Date</h3>
                <p className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  {organisation.created_at ? (
                    <>{format(new Date(organisation.created_at), 'MMMM d, yyyy')}</>
                  ) : (
                    <span className="text-gray-500">Not available</span>
                  )}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Anomaly Summary - ecommerce connections only, not shown for test clones */}
          {!organisation.is_test_clone && hasEcommerceConnections(organisation.connections) && (
            <AnomalySummarySection organisationId={organisation.id} orgCreatedAt={organisation.created_at} />
          )}
        </div>

        <OrganisationSidebar organisation={organisation} onConnectionRemoved={onConnectionRemoved} />
      </div>
    </div>
  );
};

const OrganisationSidebar: React.FC<OrganisationOverviewProps> = ({ organisation, onConnectionRemoved }) => {
  const hasConnections = organisation.connections && Object.keys(organisation.connections).length > 0;

  return (
    <div>
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-blue-500" />
            Point of Contact
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Name</h3>
            <p>{organisation.poc_name}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium text-gray-500">Email</h3>
            <p className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-gray-400" />
              <a
                href={`mailto:${organisation.poc_email}`}
                className="text-blue-600 hover:underline"
              >
                {organisation.poc_email}
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      {hasConnections && <ConnectionsList organisation={organisation} onConnectionRemoved={onConnectionRemoved} />}
    </div>
  );
};

const ConnectionsList: React.FC<OrganisationOverviewProps> = ({ organisation, onConnectionRemoved }) => {
  const { toast } = useToast();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<{ id: string; name: string; service: string } | null>(null);
  const [liveStatuses, setLiveStatuses] = useState<Record<string, {
    setup_state?: string;
    sync_state?: string;
    paused?: boolean;
    loading: boolean;
    rescheduled_for?: string | null;
    sync_tasks?: Array<{ message?: string; details?: string }>;
    sync_warnings?: Array<{ message?: string; details?: string }>;
    status_reason?: unknown;
  }>>({});
  const [confirmDeleteKey, setConfirmDeleteKey] = useState<string | null>(null);
  const [confirmDeleteImport, setConfirmDeleteImport] = useState<{ connectionKey: string; importId: string; fileName: string } | null>(null);
  const [removingConnectionId, setRemovingConnectionId] = useState<string | null>(null);
  const [sourceConnectionKeys, setSourceConnectionKeys] = useState<Set<string> | null>(null);
  const [sourceImportIds, setSourceImportIds] = useState<Set<string> | null>(null);

  // For test clones, fetch source org's connection keys AND csvImport IDs
  useEffect(() => {
    if (!organisation?.is_test_clone || !organisation?.source_organisation_id) {
      setSourceConnectionKeys(null);
      setSourceImportIds(null);
      return;
    }

    const fetchSourceConnections = async () => {
      const { data } = await supabase
        .from('organisations')
        .select('connections')
        .eq('id', organisation.source_organisation_id)
        .single();

      if (data?.connections && typeof data.connections === 'object') {
        const conns = data.connections as Record<string, any>;
        setSourceConnectionKeys(new Set(Object.keys(conns)));

        // Collect all csvImport IDs from source
        const importIds = new Set<string>();
        Object.values(conns).forEach((conn: any) => {
          if (Array.isArray(conn.csvImports)) {
            conn.csvImports.forEach((imp: any) => {
              if (imp.id) importIds.add(imp.id);
            });
          }
        });
        setSourceImportIds(importIds);
      }
    };

    fetchSourceConnections();
  }, [organisation?.is_test_clone, organisation?.source_organisation_id]);

  const isInheritedConnection = (key: string): boolean => {
    if (!organisation?.is_test_clone || !sourceConnectionKeys) return false;
    return sourceConnectionKeys.has(key);
  };

  const isInheritedImport = (importId: string): boolean => {
    if (!organisation?.is_test_clone || !sourceImportIds) return false;
    return sourceImportIds.has(importId);
  };

  // Fetch real-time Fivetran status for non-CSV connections
  useEffect(() => {
    if (!organisation?.connections) return;

    const entries = Object.entries(organisation.connections) as [string, any][];
    const fivetranConnections = entries.filter(([_, value]) => {
      const isCsv = value.service === 'csv' || value.type === 'csv' || value.type === 'CSV' ||
        (value.connectorId && value.connectorId.startsWith('csv-import-'));
      return !isCsv && value.service !== 'file' && value.type !== 'plaid';
    });

    if (fivetranConnections.length === 0) return;

    const initialStatuses: Record<string, any> = {};
    fivetranConnections.forEach(([_, value]) => {
      const connectorId = value.connectorId || value.id;
      initialStatuses[connectorId] = { loading: true };
    });
    setLiveStatuses(initialStatuses);

    fivetranConnections.forEach(async ([_, value]) => {
      const connectorId = value.connectorId || value.id;
      try {
        const res = await fetch(
          `${API_BASE_URL}/manage-connection-settings?action=details&connectorId=${encodeURIComponent(connectorId)}`,
          { headers: apiHeaders }
        );
        if (res.ok) {
          const data = await res.json();
          setLiveStatuses(prev => ({
            ...prev,
            [connectorId]: {
              setup_state: data.setup_state,
              sync_state: data.sync_state,
              paused: data.paused,
              loading: false,
              rescheduled_for: data.rescheduled_for ?? null,
              sync_tasks: Array.isArray(data.sync_tasks) ? data.sync_tasks : [],
              sync_warnings: Array.isArray(data.sync_warnings) ? data.sync_warnings : [],
              status_reason: data.status_reason,
            }
          }));
        } else {
          setLiveStatuses(prev => ({ ...prev, [connectorId]: { loading: false } }));
        }
      } catch (err) {
        console.error(`Failed to fetch status for connector ${connectorId}:`, err);
        setLiveStatuses(prev => ({ ...prev, [connectorId]: { loading: false } }));
      }
    });
  }, [organisation?.connections]);

  const getStatusIndicator = (value: any, connectorId: string, isCsvConnection: boolean): {
    color: string;
    label: string;
    detail?: string | null;
    rescheduledFor?: string | null;
  } => {
    if (isCsvConnection) return { color: 'bg-green-500', label: 'Connected' };

    const live = liveStatuses[connectorId];
    if (live?.loading) return { color: 'bg-gray-300 animate-pulse', label: 'Checking...' };

    if (live?.setup_state) {
      if (live.setup_state === 'broken') return { color: 'bg-red-500', label: 'Broken' };
      if (live.setup_state === 'incomplete') return { color: 'bg-yellow-500', label: 'Incomplete' };
      if (live.paused) return { color: 'bg-yellow-500', label: 'Paused' };
      if (isRescheduledSyncState(live.sync_state)) {
        const detail = formatFivetranSyncDetail({
          syncState: live.sync_state,
          rescheduledFor: live.rescheduled_for,
          tasks: live.sync_tasks,
          warnings: live.sync_warnings,
          statusReason: live.status_reason,
        });
        return {
          color: 'bg-blue-500',
          label: 'Syncing (rescheduled)',
          detail,
          rescheduledFor: live.rescheduled_for ?? null,
        };
      }
      if (live.sync_state === 'syncing') return { color: 'bg-blue-500', label: 'Syncing' };
      return { color: 'bg-green-500', label: 'Connected' };
    }

    // Plaid connections: check stored status directly (no live Fivetran poll)
    if (value.type === 'plaid' || value.service === 'plaid') {
      const hasToken = !!value.metadata?.access_token;
      const s: string | undefined =
        typeof value.status === 'string' ? value.status : undefined;
      if (!hasToken || s === 'expiring') {
        return {
          color: 'bg-orange-400',
          label: 'Needs Re-authentication',
          detail: 'Bank connection credentials have expired. Re-authenticate to keep data syncing.',
        };
      }
      if (s === 'error') {
        return {
          color: 'bg-red-500',
          label: 'Authentication Error',
          detail: value.metadata?.error?.error_message || 'Plaid reported an error. Re-authenticate to restore the connection.',
        };
      }
      if (s === 'disconnected') {
        return {
          color: 'bg-red-500',
          label: 'Disconnected',
          detail: 'The user revoked bank access. A new connection must be authorised.',
        };
      }
      const isActive = s === 'active' || s === 'connected';
      return { color: isActive ? 'bg-green-500' : 'bg-yellow-500', label: isActive ? 'Connected' : 'Incomplete' };
    }

    const isConnected = value.status === 'connected';
    return { color: isConnected ? 'bg-green-500' : 'bg-yellow-500', label: isConnected ? 'Connected' : 'Incomplete' };
  };

  const handleDeleteConnection = async (key: string, value: any) => {
    const connectorId = value.connectorId || value.id || key;
    setRemovingConnectionId(key);

    try {
      const { data, error } = await invokeFunction(supabase, 'remove-data-connector', {
        body: {
          connectorId,
          connectionId: value.connectionId || value.id || key,
          organisationId: organisation.id,
          deleteDatabase: true,
        }
      });

      if (error) throw error;

      toast({
        title: 'Connection removed',
        description: `${value.name || key} has been removed and its database deleted.`,
      });

      onConnectionRemoved?.();
    } catch (err: unknown) {
      console.error('Failed to remove connection:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to remove connection',
        variant: 'destructive',
      });
    } finally {
      setRemovingConnectionId(null);
      setConfirmDeleteKey(null);
    }
  };

  const handleDeleteCsvImport = async (connectionKey: string, importId: string, fileName: string) => {
    setRemovingConnectionId(importId);

    try {
      const connection = organisation.connections[connectionKey];
      const csvImports = connection.csvImports || [];
      const importToDelete = csvImports.find((imp: any) => imp.id === importId);

      const updatedImports = csvImports.filter((imp: any) => imp.id !== importId);

      const sourceConnections = organisation.connections;
      let updatedConnections: Record<string, any> | any[];

      if (Array.isArray(sourceConnections)) {
        // Preserve array shape: connectionKey is the string index (Object.entries on an array yields "0","1",...).
        const idx = Number(connectionKey);
        if (updatedImports.length === 0) {
          updatedConnections = [
            ...sourceConnections.slice(0, idx),
            ...sourceConnections.slice(idx + 1),
          ];
        } else {
          updatedConnections = sourceConnections.map((c, i) =>
            i === idx ? { ...connection, csvImports: updatedImports } : c
          );
        }
      } else {
        if (updatedImports.length === 0) {
          const copy = { ...sourceConnections };
          delete copy[connectionKey];
          updatedConnections = copy;
        } else {
          updatedConnections = {
            ...sourceConnections,
            [connectionKey]: {
              ...connection,
              csvImports: updatedImports,
            },
          };
        }
      }

      const { error } = await supabase
        .from('organisations')
        .update({ connections: updatedConnections })
        .eq('id', organisation.id);

      if (error) throw error;

      const parentConnectionId = (connection.connectionId || connection.id || connectionKey) as string;
      const snapshotConn = importToDelete?.snapshot_connection_id as string | undefined;
      const snapshotIdForCleanup =
        updatedImports.length === 0 ? parentConnectionId : snapshotConn;
      const jobId = importToDelete?.csv_import_job_id as string | undefined;
      const snapDate =
        (importToDelete?.snapshot_date as string | undefined) ||
        parseSnapshotDateFromInventoryImportId(importId);
      const invSource =
        (importToDelete?.inventory_source as string | undefined) ||
        inventorySourceFromEcommerceSubtype(connection);
      const hasVarianceKeys = Boolean(snapDate && invSource);

      if (snapshotIdForCleanup || jobId || hasVarianceKeys) {
        try {
          const { data: cleanupData, error: cleanupError } = await invokeFunction<{
            success?: boolean;
            error?: string;
          }>(supabase, 'cleanup-csv-import-artifacts-v1', {
            body: {
              organisation_id: organisation.id,
              ...(snapshotIdForCleanup && { snapshot_connection_id: snapshotIdForCleanup }),
              ...(hasVarianceKeys && {
                snapshot_date: snapDate,
                inventory_source: invSource,
              }),
              ...(jobId && { csv_import_job_id: jobId }),
            },
          });
          if (cleanupError) {
            console.warn('Non-blocking: cleanup-csv-import-artifacts-v1 failed:', cleanupError);
          } else if (cleanupData && typeof cleanupData === 'object' && 'success' in cleanupData && !cleanupData.success) {
            console.warn('Non-blocking: cleanup-csv-import-artifacts-v1:', cleanupData);
          }
        } catch (cleanupErr) {
          console.warn('Non-blocking: cleanup-csv-import-artifacts-v1 error:', cleanupErr);
        }
      }

      toast({
        title: 'Import removed',
        description: `${fileName} has been removed.`,
      });

      onConnectionRemoved?.();
    } catch (err: unknown) {
      console.error('Failed to remove CSV import:', err);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to remove import',
        variant: 'destructive',
      });
    } finally {
      setRemovingConnectionId(null);
      setConfirmDeleteImport(null);
    }
  };

  const connectionToDelete = confirmDeleteKey ? organisation.connections[confirmDeleteKey] : null;

  // Build flat list of renderable rows
  const renderableRows: Array<{
    type: 'connection' | 'csv-import';
    key: string;
    connectionKey: string;
    value: any;
    displayName: string;
    isCsv: boolean;
    importId?: string;
  }> = [];

  Object.entries(organisation.connections).forEach(([key, value]: [string, any]) => {
    if (value.service === 'file') return;

    const isCsv = isCsvConnection(value);

    if (isCsv && Array.isArray(value.csvImports) && value.csvImports.length > 0) {
      // Expand each csvImport as its own row
      value.csvImports.forEach((imp: any) => {
        renderableRows.push({
          type: 'csv-import',
          key: `${key}_${imp.id}`,
          connectionKey: key,
          value,
          displayName: imp.fileName || imp.id || 'Unnamed import',
          isCsv: true,
          importId: imp.id,
        });
      });
    } else {
      renderableRows.push({
        type: 'connection',
        key,
        connectionKey: key,
        value,
        displayName: value.name || key,
        isCsv,
      });
    }
  });

  return (
    <TooltipProvider>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-500" />
            Data Connections
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {renderableRows.map((row) => {
              const connectorId = row.value.connectorId || row.connectionKey;
              const statusInfo = getStatusIndicator(row.value, connectorId, row.isCsv);
              const isRemoving = removingConnectionId === (row.importId || row.connectionKey);

              return (
                <li key={row.key} className="flex items-center justify-between gap-2 p-2 rounded-md bg-gray-50">
                  <div className="flex flex-col min-w-0 flex-1 gap-0.5">
                    <div className="flex items-center gap-2">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${statusInfo.color}`}></div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          <p className="font-medium">{statusInfo.label}</p>
                          {statusInfo.detail ? (
                            <p className="text-xs mt-1 text-muted-foreground">{statusInfo.detail}</p>
                          ) : null}
                          {statusInfo.rescheduledFor && !statusInfo.detail?.includes('Next sync') ? (
                            <p className="text-xs mt-1">
                              {formatRescheduledForDisplay(statusInfo.rescheduledFor) ?? statusInfo.rescheduledFor}
                            </p>
                          ) : null}
                        </TooltipContent>
                      </Tooltip>
                      <span className="text-sm truncate max-w-[200px]" title={row.displayName}>{row.displayName}</span>
                    </div>
                    {statusInfo.label === 'Syncing (rescheduled)' && statusInfo.detail ? (
                      <p className="text-xs text-muted-foreground pl-4 truncate" title={statusInfo.detail}>
                        {statusInfo.detail}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex items-center gap-1 shrink-0">

                    {/* Settings - only for non-CSV, non-plaid, non-file Fivetran connections */}
                    {!row.isCsv && row.value.type !== "plaid" && row.value.service !== "file" && connectorId && row.type === 'connection' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0"
                            onClick={() => {
                              setSelectedConnector({ id: connectorId, name: row.displayName, service: row.value.service || row.value.type || '' });
                              setSettingsOpen(true);
                            }}
                          >
                            <Settings className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Connection Settings</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Fivetran link - only for non-CSV, non-plaid, non-file connections */}
                    {!row.isCsv && row.value.type !== "plaid" && row.value.service !== "file" && connectorId && row.type === 'connection' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                            asChild
                          >
                            <a
                              href={`https://fivetran.com/dashboard/connections/${connectorId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={`View ${row.displayName} in Fivetran`}
                            >
                              <span>View in Fivetran</span>
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs">Connection ID: figabl_{row.value.connectionId}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {/* Delete button */}
                    {row.type === 'csv-import' && (
                      // CSV import row: only show delete if not inherited
                      (() => {
                        const inherited = isInheritedImport(row.importId!);
                        if (inherited) return null;
                        return (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                disabled={isRemoving}
                                onClick={() => setConfirmDeleteImport({ connectionKey: row.connectionKey, importId: row.importId!, fileName: row.displayName })}
                              >
                                {isRemoving ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Trash2 className="h-3.5 w-3.5" />
                                )}
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Remove Import</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })()
                    )}

                    {row.type === 'connection' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            disabled={isRemoving || isInheritedConnection(row.connectionKey)}
                            onClick={() => setConfirmDeleteKey(row.connectionKey)}
                          >
                            {isRemoving ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{isInheritedConnection(row.connectionKey) ? 'Cannot remove connections inherited from source organisation' : 'Remove Connection'}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      {/* Confirm delete entire connection */}
      <AlertDialog open={!!confirmDeleteKey} onOpenChange={(open) => !open && setConfirmDeleteKey(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {connectionToDelete?.name || confirmDeleteKey}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently disconnect this data source and delete its associated database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!removingConnectionId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!!removingConnectionId}
              onClick={() => {
                if (confirmDeleteKey) {
                  handleDeleteConnection(confirmDeleteKey, organisation.connections[confirmDeleteKey]);
                }
              }}
            >
              {removingConnectionId ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Removing...</>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm delete individual CSV import */}
      <AlertDialog open={!!confirmDeleteImport} onOpenChange={(open) => !open && setConfirmDeleteImport(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {confirmDeleteImport?.fileName}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this imported file from the connection. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!removingConnectionId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={!!removingConnectionId}
              onClick={() => {
                if (confirmDeleteImport) {
                  handleDeleteCsvImport(confirmDeleteImport.connectionKey, confirmDeleteImport.importId, confirmDeleteImport.fileName);
                }
              }}
            >
              {removingConnectionId ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Removing...</>
              ) : (
                'Remove'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {selectedConnector && (
        <OrgConnectionSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          connectorId={selectedConnector.id}
          connectionName={selectedConnector.name}
          service={selectedConnector.service}
          orgId={organisation.id}
          orgConnections={organisation.connections}
        />
      )}
    </TooltipProvider>
  );
};

export default OrganisationOverview;
export { OrganisationSidebar, ConnectionsList };
